import React from 'react';
import PropTypes from 'prop-types';

import { BFLoader } from '@components/common/loader/main';
import AttachmentSidebar from '../shared/attachment_sidebar/main';
import VisibleAttachment from '../shared/visible_attachment/main';
import AttachmentCard from '../../../data/attachment_card';

import './styles/main.scss';

class SimilarityTab extends React.Component {
  state = {
    ajaxCalls: [],
    attachments: [],
    brandfolderKey: '',
    isLoadingSimilarByFeatures: false,
    loading: true,
    organizationKey: '',
    similarityMaxAttachments: 50,
    similarityScoreThreshold: 1000000,
    visibleAttachmentId: null,
    visibleDuplicate: [],
    visibleSimilar: []
  };

  /* NOTE :: When you have a parent modal component that grabs the asset info
  you won't need to grab the attachments on each tab, you can grab them when
  you load the modal and then just pass them down. Attachments will also no
  longer need to be stored in state when this happens */
  componentDidMount() {
    this.addTabListener();
  }

  componentWillUnmount() {
    this.removeTabListener();
    this.abortAjaxCalls();
  }

  getDuplicates() {
    const { brandfolderKey, visibleAttachmentId } = this.state;
    const paramsString = 'include=asset&fields=extension,thumbnail_url';

    const request = $.ajax({
      context: this,
      type: 'GET',
      url: `/api/v4/brandfolders/${brandfolderKey}/attachments/${visibleAttachmentId}/duplicate?${paramsString}`,
      beforeSend: (xhr) => {
        xhr.setRequestHeader("Authorization", `Bearer ${BF_Token}`);
      },
      success: (response) => {
        this.setState({ visibleDuplicate: response.data }, () => this.getSimilarByPerceptual());
      },
      error: () => {
        // TODO Create error state
        this.setState({ loading: false });
      }
    });

    this.updateAjaxCalls(request);
  }

  getSimilarByPerceptual() {
    const { brandfolderKey, visibleAttachmentId } = this.state;
    const paramsString = 'include=asset&fields=extension,thumbnail_url';

    const request = $.ajax({
      url: `/api/v4/brandfolders/${brandfolderKey}/attachments/${visibleAttachmentId}/similar?${paramsString}`,
      method: 'GET',
      context: this,
      beforeSend: (xhr) => {
        xhr.setRequestHeader("Authorization", `Bearer ${BF_Token}`);
      },
      success: (response) => {
        const similarAttachmentsNoDups = [];
        const similarAttachmentKeys = response.data.map((attachment) => attachment.id);
        const duplicateAttachmentKeys = this.state.visibleDuplicate.map((attachment) => attachment.id);
        const similarAttachmentKeysNoDups = this.filterDuplicateKeys(similarAttachmentKeys, duplicateAttachmentKeys);
        response.data.forEach((attachment) => {
          if (similarAttachmentKeysNoDups.indexOf(attachment.id) > -1) {
            similarAttachmentsNoDups.push(attachment);
          }
        });
        this.setState({
          visibleSimilar: similarAttachmentsNoDups,
          loading: false
        }, () => {
          this.setState({ isLoadingSimilarByFeatures: true }, () => {
            if (BF_Environment !== 'development') { // eslint-disable-line no-undef
              this.getSimilarByFeatures();
            } else {
              this.setState({ isLoadingSimilarByFeatures: false });
            }
          });
        });
      },
      error: () => {
        // TODO Create error state
        this.setState({ loading: false });
      }
    });
    this.updateAjaxCalls(request);
  }

  getSimilarByFeatures() {
    const { assetType, userGeneratedTags } = this.props;
    const { organizationKey, visibleAttachmentId } = this.state;
    const { extracted_tag_names, extracted_text, extracted_colors, view_thumbnail_retina, folder_path } = this.currentAttachment().attributes;

    const request = $.ajax({
      url: 'https://similar-attachments.brandfolder-svc.com/v1/similarorgattachments',
      method: 'POST',
      context: this,
      data: JSON.stringify({ attachment: {
        asset_type: assetType,
        org_key: organizationKey,
        attachment_key: visibleAttachmentId,
        url: view_thumbnail_retina,
        user_generated_tags: Array.isArray(userGeneratedTags) ? userGeneratedTags.join(", ") : "",
        tags: Array.isArray(extracted_tag_names) ? extracted_tag_names.join(", ") : "",
        extracted_colors: Array.isArray(extracted_colors) ? extracted_colors[0].color.hex_color : "",
        extracted_text: (typeof extracted_text === "string") ? extracted_text : "",
        folder_path: (typeof folder_path === "string") ? folder_path : "" }
      }),
      contentType: 'application/json',
      beforeSend: (xhr) => {
        xhr.setRequestHeader("Authorization", `Bearer ${BF_Token}`);
      },
      success: (response) => {
        if (response && response.similarattachments && response.similarattachments.attachments) {
          const { similarityScoreThreshold, similarityMaxAttachments, visibleDuplicate, visibleSimilar } = this.state;
          const dupKeys = visibleDuplicate.map((attachment) => attachment.id);
          const simKeys = visibleSimilar.map((attachment) => attachment.id);
          const visibleDupSimKeys = [visibleAttachmentId, ...dupKeys, ...simKeys];
          const similarAttachments = response.similarattachments.attachments;
          const similarKeysByFeature = [];
          // remove attachments below similarity score threshold and create array of keys
          similarAttachments.sort((a, b) => a.similarity_score - b.similarity_score);
          similarAttachments.forEach((attachment) => {
            if (attachment.similarity_score <= similarityScoreThreshold) {
              similarKeysByFeature.push(attachment.attachment_key);
            }
          });
          // remove attachment keys if already shown as visible attachment, dup or simlar
          const similarKeysNoDups = this.filterDuplicateKeys(similarKeysByFeature, visibleDupSimKeys);
          // remove duplicate keys within this set
          const similarKeysNoDupsUnique = [];
          similarKeysNoDups.forEach((key) => {
            if (similarKeysNoDupsUnique.indexOf(key) === -1) {
              similarKeysNoDupsUnique.push(key);
            }
          });
          // combine attachment keys and create subset per max attachment limit
          const attachmentKeysCombined = [...simKeys, ...similarKeysNoDupsUnique].slice(0, similarityMaxAttachments);
          this.getBulkAttachments(attachmentKeysCombined);
        } else {
          this.setState({ isLoadingSimilarByFeatures: false });
        }
      },
      error: () => {
        this.setState({ isLoadingSimilarByFeatures: false });
      }
    });
    this.updateAjaxCalls(request);
  }

  getBulkAttachments(attachmentKeys) {
    const { brandfolderKey } = this.state;
    const paramsString = 'include=asset&fields=extension,thumbnail_url';

    const request = $.ajax({
      url: `/api/v4/brandfolders/${brandfolderKey}/bulk_retrieve/attachments?${paramsString}`,
      method: 'POST',
      context: this,
      data: JSON.stringify({ ids: attachmentKeys }),
      contentType: 'application/json',
      beforeSend: (xhr) => {
        xhr.setRequestHeader("Authorization", `Bearer ${BF_Token}`);
      },
      success: (response) => {
        const attachmentsArray = response.data;
        const visibleSimilarSorted = [];
        if (attachmentsArray.length) {
          attachmentKeys.forEach((id) => {
            attachmentsArray.forEach((attachment) => {
              if (id === attachment.id) {
                visibleSimilarSorted.push(attachment);
              }
            });
          });
        }
        this.setState({ visibleSimilar: visibleSimilarSorted, isLoadingSimilarByFeatures: false });
      },
      error: () => {
        this.setState({ isLoadingSimilarByFeatures: false });
      }
    });
    this.updateAjaxCalls(request);
  }

  requestSimilarity = () => {
    if (this.state.loading) {
      const request = $.ajax({
        context: this,
        type: 'GET',
        url: `/api/v4/assets/${this.props.assetKey}/attachments?fields=download_url,thumbnail_url,view_thumbnail_retina,extension,thumbnailed,extracted_tag_names,extracted_text,extracted_colors,folder_path`,
        beforeSend: (xhr) => {
          xhr.setRequestHeader("Authorization", `Bearer ${BF_Token}`);
        },
        success: (response) => {
          const brandfolderKey = BF.fx.brandfolder().key;
          const organizationKey = BF.fx.organization().key;
          const sortedAttachments = response.data.sort((a, b) => a.attributes.position - b.attributes.position);

          this.setState({
            attachments: sortedAttachments,
            brandfolderKey,
            organizationKey,
            visibleAttachmentId: response.data[0].id,
          }, () => {
            this.getDuplicates();
          });
        },
        error: () => {
          // TODO Create error state
          this.setState({ loading: false });
        }
      });
      this.updateAjaxCalls(request);
    }
  }

  updateActiveImage = ({ id: attachmentId }) => {
    const { visibleAttachmentId } = this.state;

    if (visibleAttachmentId !== attachmentId) {
      this.abortAjaxCalls();
      this.setState({
        ajaxCalls: [],
        visibleAttachmentId: attachmentId,
        visibleSimilar: [],
        visibleDuplicate: [],
        loading: true
      }, () => {
        this.getDuplicates();
      });
    }
  }

  addTabListener = () => {
    window.addEventListener(
      `similarityAssetTabHover${this.props.assetKey}`,
      this.requestSimilarity,
      false
    );
  }

  removeTabListener = () => {
    window.removeEventListener(
      `similarityAssetTabHover${this.props.assetKey}`,
      this.requestSimilarity,
      false
    );
  }

  updateAjaxCalls = (xhr) => {
    this.setState((prevState) => {
      const updatedAjaxCalls = [...prevState.ajaxCalls];
      updatedAjaxCalls.push(xhr);

      return { ajaxCalls: updatedAjaxCalls };
    });
  }

  abortAjaxCalls = () => {
    this.state.ajaxCalls.forEach((ajaxCall) => {
      if (ajaxCall.readyState !== 4) {
        ajaxCall.abort();
      }
    });
  }

  filterDuplicateKeys = (similarAttachmentKeys, keysToFilterOut) => {
    const uniqueKeys = [];
    similarAttachmentKeys.forEach((simKey) => {
      if (keysToFilterOut.indexOf(simKey) === -1) {
        uniqueKeys.push(simKey);
      }
    });
    return uniqueKeys;
  }

  currentAttachment = () => (
    this.state.attachments.filter((attachment) => attachment.id === this.state.visibleAttachmentId)[0]
  )

  renderDuplicates = () => (
    this.state.visibleDuplicate.map((duplicate) => (
      <AttachmentCard
        key={duplicate.id}
        attachment={duplicate.attributes}
        assetKey={duplicate.relationships.asset.data.id}
        brandfolderSlug={BF.fx.brandfolder().slug}
      />
    ))
  )

  renderSimilar = () => {
    const visibleSimilarArray = [];

    this.state.visibleSimilar.forEach((similar) => {
      visibleSimilarArray.push(
        <AttachmentCard
          key={similar.id}
          attachment={similar.attributes}
          assetKey={similar.relationships.asset.data.id}
          brandfolderSlug={BF.fx.brandfolder().slug}
        />
      );
    });

    if (this.state.isLoadingSimilarByFeatures) {
      visibleSimilarArray.push(this.similarLoading());
    }

    return visibleSimilarArray;
  }

  renderNoSimilar = () => {
    if (this.state.isLoadingSimilarByFeatures) {
      return this.similarLoading();
    }

    return (<p className="gray-text">{t('No similar images detected')}</p>);
  }

  similarLoading = () => (
    <li className="attachment-card-component loading-card" key="key">
      <p>{t("Looking for more similar images")}</p>
      <p className="dot dot-one">.</p>
      <p className="dot dot-two">.</p>
      <p className="dot dot-three">.</p>
      <p className="dot dot-four">.</p>
    </li>
  )

  render() {
    const { attachments, visibleAttachmentId, loading, visibleDuplicate, visibleSimilar } = this.state;
    const visibleAttachmentObject = visibleAttachmentId ? attachments.find((attachment) => attachment.id === visibleAttachmentId) : null;
    const activeCard = visibleAttachmentId
      ? { id: visibleAttachmentId, type: 'attachment' }
      : null;

    return (
      <div className="flex-wrapper similar-tab-container">
        <AttachmentSidebar
          attachments={attachments.length ? attachments : null}
          updateActiveCard={this.updateActiveImage}
          activeCard={activeCard}
        />
        {loading ? (
          <div className="flex-two-thirds no-similar-details loader-container">
            <BFLoader />
          </div>
        ) : (
          <React.Fragment>
            <div className={`flex-two-thirds ${visibleDuplicate.length > 0 ? 'similar-details' : 'no-similar-details'}`}>
              {visibleAttachmentObject && visibleAttachmentObject.attributes
                ? (
                  <VisibleAttachment
                    extension={visibleAttachmentObject.attributes.extension}
                    filename={visibleAttachmentObject.attributes.filename}
                    height={visibleAttachmentObject.attributes.height}
                    size={visibleAttachmentObject.attributes.size}
                    thumbnail={visibleAttachmentObject.attributes.thumbnail_url}
                    width={visibleAttachmentObject.attributes.width}
                  />
                ) : ''
              }
              {visibleAttachmentObject && visibleAttachmentObject.attributes
                ? (
                  <h3 className="title">{t('Duplicates of ')}
                    <span className="title-bold">{visibleAttachmentObject.attributes.filename}</span>
                  </h3>
                )
                : ''
              }
              {visibleDuplicate.length > 0
                ? (
                  <React.Fragment>
                    <div className="card-list card-list-duplicates t-duplicates-container">
                      {this.renderDuplicates()}
                    </div>
                    <hr />
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <div className="card-list-empty t-duplicates-container">
                      <p className="gray-text">{t('No duplicate images detected')}</p>
                    </div>
                    <hr />
                  </React.Fragment>
                )
              }
              {visibleAttachmentObject && visibleAttachmentObject.attributes
                ? (
                  <h3 className="title">
                    {t('Similar to ')}
                    <span className="title-bold">
                      {visibleAttachmentObject.attributes.filename}
                    </span>
                  </h3>
                ) : ''
              }
              {visibleSimilar.length > 0
                ? <div className="card-list t-similar-container"> {this.renderSimilar()} </div>
                : <div className="t-similar-container">{this.renderNoSimilar()}</div>
              }
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

SimilarityTab.propTypes = {
  assetKey: PropTypes.string.isRequired,
  assetType: PropTypes.string.isRequired,
  userGeneratedTags: PropTypes.arrayOf(PropTypes.string)
};

SimilarityTab.defaultProps = {
  userGeneratedTags: []
};

export default SimilarityTab;
